import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useEffectEvent } from "@/hooks/useEffectEvent";
import {
  useLazyGetAutomationStreamingProjectQuery,
  useRequestAutomationStreamingCaptionMutation,
  useLazyGetAutomationStreamingCaptionQuery,
  useCompleteAutomationStreamingProjectMutation,
  useUpdateAutomationStreamingProjectMutation,
  useSealAutomationStreamingProjectMutation,
  useUpdateAutomationStreamingThumbnailMutation,
  useCreateAutomationStreamingProjectMutation,
  useResetAutomationStreamingProjectMutation,
  useExportEarlyAutomationStreamingProjectMutation,
  useLazyGetMediaInfoQuery,
} from "@/redux/api/automationApi";
import { useUploadAutomationMutation } from "@/redux/api/dashboardApi";
import {
  createFabricCanvas,
  initCanvasSize,
  getHeadOnly,
  initCanvasScene,
  thumbnailScene,
} from "@/lib/canvas-utils";
import { genSceneId } from "@/lib/clip-utils";
import { base64ToBlob } from "@/lib/base64-to-blob-utils";
import { PromiseWithResolver } from "@/lib/promise-with-resolver";
import { SCENE_CONFIG_MAP } from "@/constants/automation/scene-config-map";
import { preCheckUrl } from "@/lib/automation-precheck-utils";
import { preCheckTopic } from "@/lib/automation-precheck-utils";
import { ApiError } from "@/lib/api-exceptions";
import { useSubscription } from "@/hooks/useSubscription";

import dayjs from "dayjs";
import md5 from "md5";
import { cloneDeep, isEqual } from "lodash";

import type { MutableRefObject } from "react";
import type { NextRouter } from "next/router";
import type { RestApiResponseSuccess } from "@/types/common.api";
import type {
  ProjectType,
  Scene as SceneWithoutId,
} from "@/types/type";
import type {
  AutomationStreamingTemplate as Template,
  AutomationStreamingStream as Stream,
  AutomationStreamingSampleDocs,
  AutomationStreamingSampleUrl,
  AutomationStreamingDocument,
  AutomationStreamingCreateParams,
  AutomationStreamingMediaInfo,
} from "@/types/automation";
import type {
  DeferCationAction,
} from "@/hooks/usePreviewVideo";

export type Scene = Omit<SceneWithoutId, "id"> & { id: string };
type Templates = { base: Template; working: Template };
type StepResult<T> = Promise<T | null>;

type DrawImage = { [id: string]: string | null };
type DrawScript = { [id: string]: string };

type InitJobFunction = (signal: AbortSignal) => StepResult<Templates>;
type GetStreamsFunction = (templates: Templates, signal: AbortSignal) => StepResult<Scene[]>;
type UpdateProjectThumbnailFunction = (scene: Scene, orientation: "landscape" | "portrait", signal: AbortSignal) => void;
type GetCaptionsFunction = (scenes: Scene[], signal: AbortSignal) => StepResult<Scene[]>;
type FinishJobFunction = (scenes: Scene[]) => StepResult<void>;

class CaptionError extends Error {
  code: string;
  
  constructor(code: string) {
    super();
    this.code = code;
  }
}

export type UseAutomationStreamsResult = {
  scenes: (Scene | undefined)[];
  metadata: { title: string; imageUrl: string } | null;
  error: string | null;
  project: ProjectType | null;
  finish: boolean;
  sceneFinish: { [id: string]: boolean };
  eventSourceFinish: boolean;
  captionsFinish: boolean;
  updateProject: (params: { scene: Scene }) => Promise<Scene | null>;
  sealProject: () => Promise<Scene[] | null | undefined>;
  exportEarlyProject: () => Promise<RestApiResponseSuccess<void>>;
  updating: boolean;
  job: AutomationStreamingDocument["status"] | null;
  rawParams: AutomationStreamingDocument["params"] | null;
  captionActions: MutableRefObject<{ [key: string]: Promise<DeferCationAction | void> }>;
  mediaInfo: MutableRefObject<AutomationStreamingMediaInfo[]>;
}

export type UseAutomationDrawsResult = {
  width: number;
  height: number;
  orientation: "landscape" | "portrait";
  images: { [id: string]: string | null };
  scripts: { [id: string]: string };
}

export function curryingGetImage(params: {
  width: number;
  height: number;
  orientation: "landscape" | "portrait";
}) {
  const { width, height, orientation } = params;

  // const { width, height } = (() => {
  //   if (orientation === "landscape") {
  //     return { width: 800, height: 450 }
  //   }
  //   if (orientation === "portrait") {
  //     return { width: 254, height: 450 }
  //   }
  //   return { width: params.width, height: params.height };
  // })();

  return async (scene: Scene) => {
    const canvas = createFabricCanvas(null, { 
      backgroundColor: "white",
    }, true);
    canvas.name = "automation-streaming-canvas";
  
    initCanvasSize({ canvas, width, height, orientation });
  
    const reScene: Scene = {
      ...scene,
      clips: await Promise.all(scene.clips.map(async (v) => {
        if (
          v.type === "aiModel" &&
          v.headOnly &&
          !v.headOnly.src &&
          v.isDelete !== true
        ) {
          return {
            ...v,
            headOnly: await getHeadOnly({ clip: v, shape: v.headOnly.shape })
          };
        }
        return v;
      }))
    };
    
    await initCanvasScene({
      canvas,
      scene: reScene,
      evented: false,
      smooth: true
    });
  
    const data = canvas.toDataURL();

    canvas.dispose();
  
    return base64ToBlob<string>({ base64: data });
  }
}

export function applyStream(params: {
  scenes: (Scene | undefined)[];
  stream: Stream;
  templates: Templates;
}) {
  const { scenes, stream, templates } = params;

  if (stream.sceneIdx === undefined || stream.templateSceneIdx === undefined) {
    return scenes;
  }

  const streamSceneIndex = stream.sceneIdx;
  const templateSceneIndex = stream.templateSceneIdx;
  
  const scene = !scenes[streamSceneIndex]
    ? {
      ...cloneDeep(templates.working.project.scenes[templateSceneIndex]),
      id: genSceneId(scenes),
    }
    : cloneDeep(scenes[streamSceneIndex]);

  if (stream.clips) {
    stream.clips.forEach((streamProps) => {
      const { _replaceId: replaceId, ...streamClip } = streamProps;
      const sceneClip = scene.clips
        .find((clip) => clip.id === streamClip.id || clip.id === replaceId);
      const baseClip = templates.base.project.scenes[templateSceneIndex].clips
        .find((clip) => clip.id === streamClip.id || clip.id === replaceId);

      if (!sceneClip && baseClip) {
        scene.clips = [
          ...scene.clips,
          {
            ...baseClip,
            ...streamClip,
            id: replaceId ? replaceId : baseClip.id,
          }
        ];
      }
      
      if (sceneClip) {
        scene.clips = scene.clips.map((clip) => {
          if (clip.id !== streamClip.id) {
            return clip;
          }

          return {
            ...clip,
            ...streamClip,
            id: replaceId ? replaceId : clip.id,
          }
        });
      }
    });
  }

  if (stream.script || stream.finalScript) {
    const script = stream.finalScript || stream.script || "";

    scene.scripts = scene.scripts
      .map((v) => ({ ...v, org: script }))
      .filter((v, i) => i === 0);
  }

  if (stream.finalScriptLang) {
    scene.scripts = scene.scripts
      .map((v) => {
        const isMultiLanguage = (() => {
          if (v.tts && v.isTTS) {
            if (v.tts.language === "multi") {
              return false;
            }

            return v.tts.language.split("-")[0] !== stream.finalScriptLang;
          }

          return v.modelSupportLanguage !== stream.finalScriptLang;
        })();

        if (!isMultiLanguage) {
          return {
            ...v,
            detectedLanguage: stream.finalScriptLang,
            detectionMarked: true,
          };
          
        }

        return {
          ...v,
          detectedLanguage: stream.finalScriptLang,
          detectionMarked: true,
          isMultiLanguage,
        };
      })
      .filter((v, i) => i === 0);
  }

  if (stream.background) {
    scene.background = {
      ...scene.background,
      source_type: "image",
      source_url: stream.background.source_url,
    };
  }

  const next = [...scenes];
  next[streamSceneIndex] = scene;

  return next;
}

export function useAutomationStreams(projectId: string): UseAutomationStreamsResult {
  const [
    scenesState,
    setScenesState,
  ] = useState<UseAutomationStreamsResult["scenes"]>([]);
  const [
    metadataState,
    setMetadataState,
  ] = useState<UseAutomationStreamsResult["metadata"]>(null);
  const [
    errorState,
    setErrorState,
  ] = useState<UseAutomationStreamsResult["error"]>(null);
  const [
    projectState,
    setProjectState,
  ] = useState<UseAutomationStreamsResult["project"]>(null);
  const [
    finishState,
    setFinishState,
  ] = useState<UseAutomationStreamsResult["finish"]>(false);
  const [
    sceneFinishState,
    setSceneFinishState,
  ] = useState<UseAutomationStreamsResult["sceneFinish"]>({});
  const [
    eventSourceFinishState,
    setEventSourceFinishState,
  ] = useState<UseAutomationStreamsResult["eventSourceFinish"]>(false);
  const [
    captionsFinishState,
    setCaptionsFinishState,
  ] = useState<UseAutomationStreamsResult["captionsFinish"]>(false);
  const [
    updatingState,
    setUpdatingState,
  ] = useState<{ [id: string]: boolean; }>({});
  const [
    jobState,
    setJobState,
  ] = useState<UseAutomationStreamsResult["job"]>(null);
  const [
    rawParamsState,
    setRawParamsState,
  ] = useState<UseAutomationStreamsResult["rawParams"]>(null);
  const captionActions = useRef<{ [key: string]: Promise<DeferCationAction | void> }>({});
  const mediaInfo = useRef<AutomationStreamingMediaInfo[]>([]);

  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [getProject] =
    useLazyGetAutomationStreamingProjectQuery({
      refetchOnReconnect: false,
      refetchOnFocus: false,
    });

  const [requestCaption] = useRequestAutomationStreamingCaptionMutation();
  const [fetchCaption] = useLazyGetAutomationStreamingCaptionQuery({
    refetchOnReconnect: false,
    refetchOnFocus: false,
  });

  const [completeProject] = useCompleteAutomationStreamingProjectMutation();
  const [updateProject] = useUpdateAutomationStreamingProjectMutation();
  const [sealProject] = useSealAutomationStreamingProjectMutation();
  const [updateThumbnail] = useUpdateAutomationStreamingThumbnailMutation();
  const [exportEarlyProject] = useExportEarlyAutomationStreamingProjectMutation();
  const [getMediaInfo] = useLazyGetMediaInfoQuery();

  const mergedUpdatingState = useMemo(() => {
    if (Object.values(updatingState).some((v) => v)) {
      return true;
    }
    return false;
  }, [updatingState]);

  const initJob: InitJobFunction = useCallback(
    async (signal) => {
      const response = await getProject(projectId).unwrap();

      if (signal.aborted) {
        return null;
      }

      if (!response.success) {
        const e = response.error?.code || "AS2701"; // init job error
        setErrorState(e);
        return Promise.reject(new Error(e));
      }

      const { project, job } = response.data;
      const { working, base } = job.templates;
      const { status, mediaInfo: mInfo } = job;
      const { error, finish } = status;

      if (error) {
        const e = error;
        setErrorState(e);
        return Promise.reject(new Error(e));
      }

      const scenes = project.scenes as Scene[];

      setProjectState(project);
      setJobState(job.status);
      setRawParamsState({ ...job.params });

      if (finish) {
        setScenesState(scenes);
        setFinishState(true);
        setSceneFinishState(scenes.reduce((acc, scene) => ({
          ...acc,
          [scene.id]: true,
        }), {}));
        setEventSourceFinishState(true);
        setCaptionsFinishState(true);
        mediaInfo.current = mInfo;
        return null;
      }

      return { base, working };
    },
    [getProject, projectId]
  );

  const applyCaption = useCallback(async (params: {
    scene: Scene;
    signal: AbortSignal;
  }) => {
    const { scene, signal } = params;

    const captionClip = scene.clips.find((clip) => clip.type === "captions");

    if (!captionClip) {
      return scene;
    }

    const taskIdResponse = await requestCaption({ projectId, scene }).unwrap();

    if (signal.aborted) {
      return scene;
    }

    if (!taskIdResponse.success) {
      const e = taskIdResponse.error.code;
      throw new CaptionError(e);
    }

    const taskId = taskIdResponse.data.taskId;

    while (true) {
      if (signal.aborted) {
        return scene;
      }

      await new Promise((r) => setTimeout(r, 1000));

      const captionResponse = await fetchCaption({ projectId, taskId }).unwrap();

      if (!captionResponse.success) {
        const e = captionResponse.error.code;
        throw new CaptionError(e);
      }

      if (captionResponse.data.error) {
        const e = captionResponse.data.error.code;
        throw new CaptionError(e);
      }

      if (captionResponse.data.timestamp) {
        const tempo = Number(scene.tempo || 1);
        const caption = captionResponse.data.timestamp;

        return {
          ...scene,
          clips: scene.clips.map((clip) => {
            if (clip.type !== "captions") {
              return clip;
            }

            return {
              ...clip,
              text: caption[0].caption,
              captions: caption.map((v, i, a) => {
                const start = Number((v.start / tempo).toFixed(2));
                const end = Number((v.end / tempo).toFixed(2));

                if (!a[i + 1]) {
                  return { ...v, start, end };
                }

                const nextStart = Number((a[i + 1].start / tempo).toFixed(2));

                if (end > nextStart) {
                  return { ...v, start, end: nextStart };
                }

                return { ...v, start, end };
              }),
            }
          }),
        }
      }
    }
  }, [fetchCaption, projectId, requestCaption]);

  const getCaptionAction = useCallback(async (params: {
    scene: Scene;
    signal: AbortSignal;
  }): Promise<DeferCationAction | void> => {
    const { scene, signal } = params;

    const captionClip = scene.clips.find((clip) => clip.type === "captions");

    if (!captionClip) {
      return;
    }

    try {
      const appliedScene = await applyCaption({ scene, signal });

      if (signal.aborted) {
        return;
      }

      const appliedCaptionClip = appliedScene.clips.find((clip) => clip.type === "captions");

      if (!appliedCaptionClip) {
        throw new Error();
      }

      return {
        action: "edit",
        clip: appliedCaptionClip,
      }
    } catch (e) {
      console.error(e);

      return {
        action: "delete",
        clip: captionClip,
      }
    }
  }, [applyCaption]);

  const onGetCaptionAction = useEffectEvent((params: {
    scene: Scene;
    signal: AbortSignal;
  }) => getCaptionAction(params));

  const getStreams: GetStreamsFunction = useCallback(
    (templates, signal) => {
      const scenes: (Scene | undefined)[] = [];

      const { promise, resolve, reject } = new PromiseWithResolver<Scene[] | null>();

      const eventSource = new EventSource(`/api/automation/streaming/${projectId}?request_type=stream`);
      eventSourceRef.current = eventSource;

      function onMessage(event: MessageEvent<string>) {
        if (signal.aborted) {
          eventSource.close();
          resolve(null);
        }

        const stream: Stream = JSON.parse(event.data);

        if (stream.urlMetadata) {
          setMetadataState(stream.urlMetadata);
        }
        
        if (stream.sceneIdx !== undefined && stream.templateSceneIdx !== undefined) {
          const nextScenes = applyStream({ scenes, stream, templates });

          scenes.splice(0, scenes.length, ...nextScenes);

          setScenesState(nextScenes);

          if (stream.finalScript && stream.finalScriptLang) {
            const sceneIdx = stream.sceneIdx;
            const targetScene = scenes[sceneIdx];

            if (targetScene) {
              captionActions.current = {
                ...captionActions.current,
                [targetScene.id]: onGetCaptionAction({ scene: targetScene, signal }),
              }
  
              setSceneFinishState((prev) => {
                return { ...prev, [targetScene.id]: true };
              });
            }
          }
        }

        if (stream.jobStatus === "finished" || stream.jobStatus === "error") {
          eventSource.close();
        }

        if (stream.jobStatus === "error") {
          const e = stream.errorCode ? String(stream.errorCode) : "AS2702"; // job status error
          setErrorState(e);
          reject(new Error(e));
        }

        if (stream.jobStatus === "finished") {
          const nextScenes = scenes
            .filter((scene) => !!scene)
            .map((scene) => {
              return {
                ...scene,
                clips: (
                  scene.clips
                    .map((clip) => {
                      if (clip.type === "image") {
                        delete clip.video_url;
                        delete clip.volume;
                        delete clip.loop;
                      }

                      if (
                        clip.type === "image" && (
                          clip.tag === "image" ||
                          clip.tag === "video"
                        )
                      ) {
                        clip.tag = "image";
                        clip.id = clip.id.replace("videoImage-", "image-");
                      }

                      if (
                        clip.type === "videoImage" && (
                          clip.tag === "image" ||
                          clip.tag === "video"
                        )
                      ) {
                        clip.tag = "video";
                        clip.id = clip.id.replace("image-", "videoImage-");
                      }

                      clip.name = clip.id;

                      return clip;
                    })
                    .sort((a, b) => a.layer - b.layer)
                )
              }
            });

          captionActions.current = Object.entries(captionActions.current)
            .reduce((acc, [key, value]) => {
              if (nextScenes.some((s) => s.id === key)) {
                return { ...acc, [key]: value };
              }

              return acc;
            }, {});

          setScenesState(nextScenes);
          setSceneFinishState(nextScenes.reduce((acc, scene) => ({
            ...acc,
            [scene.id]: true,
          }), {}));
          setEventSourceFinishState(true);
          resolve(nextScenes);
        }
      }

      function onError(e: Event) {
        console.error(e);
      }

      eventSource.addEventListener("message", onMessage);
      eventSource.addEventListener("error", onError);

      return promise;
    },
    [projectId]
  );

  const updateProjectThumbnail: UpdateProjectThumbnailFunction = useCallback(
    async (scene, orientation, signal) => {
      try {
        const { width, height } = (() => {
          if (orientation === "portrait") {
            return { width: 152, height: 270 }
          }
  
          return { width: 480, height: 270 }
        })();
  
        const thumbnail = await thumbnailScene<string>({
          scene,
          width,
          height,
          orientation,
          reverseAxis: false,
          canvasName: "automation-streaming-update-thumbnail",
          exportType: "base64",
        });
  
        if (signal.aborted) {
          return;
        }
  
        await updateThumbnail({ projectId, thumbnail }).unwrap();
      } catch (e) {
        console.error(e);
      }
    },
    [projectId, updateThumbnail]
  );

  const getCaptions: GetCaptionsFunction = useCallback(
    async (scenes, signal) => {
      const appliedScenes = await Promise.all(scenes.map(async (scene) => {
        if (!Object.keys(captionActions.current).includes(scene.id)) {
          return scene;
        }

        const action = await captionActions.current[scene.id];

        if (signal.aborted) {
          return Promise.reject();
        }

        if (action) {
          if (action.action === "edit") {
            return {
              ...scene,
              clips: scene.clips.map((clip) => {
                if (clip.id === action.clip.id) {
                  return action.clip;
                }

                return clip;
              }),
            }
          }

          if (action.action === "delete") {
            return {
              ...scene,
              clips: scene.clips.filter((clip) => (
                clip.id !== action.clip.id
              )),
            }
          }
        }

        return scene;
      })).then((r) => r).catch(() => null);

      if (signal.aborted) {
        return null;
      }

      if (!appliedScenes) {
        return null;
      }

      setScenesState(appliedScenes);
      setCaptionsFinishState(true);

      return appliedScenes;
    },
    []
  );

  const finishJob: FinishJobFunction = useCallback(
    async (scenes) => {
      const mediaInfoResponse = await getMediaInfo({ projectId }).unwrap();

      const mInfo = (() => {
        if (!mediaInfoResponse.success) {
          return [];
        }

        const mediaInfoResponseData = mediaInfoResponse.data;

        return (
          scenes.reduce((acc: AutomationStreamingMediaInfo[], scene, index) => {
            const sceneMediaInfoInput = mediaInfoResponseData.input[index] || "";

            const sceneMediaInfos = scene.clips
              .filter((clip) => clip.tag === "image" || clip.tag === "video")
              .map((clip) => ({
                clipId: clip.id,
                sceneId: scene.id || "",
                mediaResourceType: mediaInfoResponseData.mediaResourceType,
                generativeMediaStyle: mediaInfoResponseData.generativeMediaStyle,
                input: sceneMediaInfoInput,
              }));

            return [
              ...acc,
              ...sceneMediaInfos,
            ]
          }, [])
        );
      })();

      mediaInfo.current = mInfo;

      const completeResponse = await completeProject({
        projectId,
        scenes,
        mediaInfo: mInfo,
      }).unwrap();

      if (!completeResponse.success) {
        const e = completeResponse.error.code;
        setErrorState(e);
        return Promise.reject(new Error(e));
      }

      setFinishState(true);
    },
    [projectId, completeProject, getMediaInfo]
  );

  const start = useCallback(async () => {
    try {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const initJobResult = await initJob(abortController.signal);
  
      if (!initJobResult) {
        return;
      }
  
      const getStreamsResult = await getStreams(initJobResult, abortController.signal);
  
      if (!getStreamsResult) {
        return;
      }

      updateProjectThumbnail(
        getStreamsResult[0],
        initJobResult.working.project.orientation as ("landscape" | "portrait"),
        abortController.signal
      )
  
      const getCaptionsResult = await getCaptions(getStreamsResult, abortController.signal);
  
      if (!getCaptionsResult) {
        return;
      }
  
      await finishJob(getCaptionsResult);
    } catch (e) {
      console.error(e);
    }
  }, [finishJob, getCaptions, getStreams, initJob, updateProjectThumbnail]);

  const pause = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    captionActions.current = {};
    mediaInfo.current = [];
    setScenesState([]);
    setMetadataState(null);
    setErrorState(null);
    setProjectState(null);
    setFinishState(false);
    setSceneFinishState({});
    setEventSourceFinishState(false);
    setCaptionsFinishState(false);
    setJobState(null);
  }, []);

  const onStart = useEffectEvent(start);
  const onPause = useEffectEvent(pause);

  useEffect(() => {
    onStart();

    return () => onPause();
  }, []);

  const runUpdateProject = useCallback(async (params: { scene: Scene }) => {
    const { scene } = params;
    const sceneId = scene.id;

    try {
      setUpdatingState((prev) => {
        return {
          ...prev,
          [sceneId]: true,
        }
      });

      if (!abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      const signal = abortControllerRef.current.signal;

      const updatedScene = await (async () => {
        const targetScene = scenesState.find((s) => s?.id === sceneId);

        if (!targetScene) {
          return scene;
        }

        if (!isEqual(targetScene.scripts, scene.scripts)) {
          const appliedScene = await applyCaption({ scene, signal });

          return appliedScene;
        }

        return scene;
      })();
      const updatedSceneId = updatedScene.id;

      captionActions.current = Object.entries(captionActions.current)
        .reduce((acc, [key, value]) => {
          if (key === updatedScene.id) {
            return acc;
          }

          return { ...acc, [key]: value }
        }, {});

      if (signal.aborted) {
        return scene;
      }

      const response = await updateProject({ projectId, scene: updatedScene }).unwrap();

      if (signal.aborted) {
        return scene;
      }

      if (!response.success) {
        setErrorState("AS2703"); // update error
        return scene;
      }

      setScenesState((prev) => {
        return prev.map((s) => {
          if (s?.id !== updatedSceneId) {
            return s;
          }

          return updatedScene;
        })
      });

      return updatedScene;
    } catch (e) {
      if (e instanceof CaptionError) {
        setErrorState(e.code);
      } else {
        setErrorState("AS2704"); // update error
      }
      return null;
    } finally {
      setUpdatingState((prev) => {
        const { [sceneId]: f1, ...rest } = prev;
        return rest;
      });
    }
  }, [applyCaption, projectId, scenesState, updateProject]);

  const onUpdateProject = useEffectEvent((params: { scene: Scene; }) => runUpdateProject(params));

  const runSealProject = useCallback(async () => {
    try {
      if (scenesState.filter((v) => !!v).length === 0) {
        return;
      }

      const scenes = scenesState as Scene[];

      setUpdatingState(() => {
        return scenes.reduce((acc, scene) => {
          return { ...acc, [scene.id]: true };
        }, {});
      });
  
      const response = await sealProject({ projectId }).unwrap();

      if (!response.success) {
        return null;
      }

      setScenesState(scenes);

      return scenes;
    } catch (e) {
      console.error(e);
      return null;
    } finally {
      setUpdatingState({});
    }
  }, [projectId, scenesState, sealProject]);

  const onSealProject = useEffectEvent(() => runSealProject());

  const runEarlyExportProject = useCallback(async () => {
    const response = await exportEarlyProject({ projectId }).unwrap();

    if (!response.success) {
      throw new ApiError(response.error.code, "error on runEarlyExportProject");
    }
    return response;
  }, [exportEarlyProject, projectId]);

  const onExportEarlyProject = useEffectEvent(() => runEarlyExportProject());

  return {
    scenes: scenesState,
    metadata: metadataState,
    error: errorState,
    project: projectState,
    finish: finishState,
    sceneFinish: sceneFinishState,
    eventSourceFinish: eventSourceFinishState,
    captionsFinish: captionsFinishState,
    updateProject: onUpdateProject,
    sealProject: onSealProject,
    exportEarlyProject: onExportEarlyProject,
    updating: mergedUpdatingState,
    job: jobState,
    rawParams: rawParamsState,
    captionActions,
    mediaInfo,
  };
}

export function useAutomationDraws(props: {
  scenes: (Scene | undefined)[];
  project: ProjectType | null;
}) {
  const { scenes: streamingScenes, project } = props;

  const { width, height, orientation } = useMemo(() => {
    if (project && project.orientation === "portrait") {
      return { width: 1080, height: 1920, orientation: "portrait" } as const;
    }
    return { width: 1920, height: 1080, orientation: "landscape" } as const;
  }, [project]);

  const getImage = useMemo(() => {
    return curryingGetImage({ width, height, orientation });
  }, [height, orientation, width]);

  const [drawImage, setDrawImage] = useState<DrawImage>({});
  const [drawScript, setDrawScript] = useState<DrawScript>({});
  const hashes = useRef<{ [id: string]: string }>({});
  const abortControllerRef = useRef<AbortController>(new AbortController());

  const setDrawScripts = useCallback((scenes: (Scene | undefined)[]) => {
    const nextDrawScript = scenes.reduce((acc: DrawScript, scene) => {
      if (!scene) {
        return acc;
      }

      const scripts = scene.scripts
        .map((script) => script.org.trim())
        .join(" ")

      return {
        ...acc,
        [scene.id]: scripts || "",
      }
    }, {});

    setDrawScript(nextDrawScript);
  }, []);

  const onSetDrawScripts = useEffectEvent((scenes: (Scene | undefined)[]) => {
    setDrawScripts(scenes);
  });

  const [drawImageQueue, setDrawImageQueue] = useState<(() => Promise<void>)[]>([]);
  const runningJob = useRef<boolean>(false);

  useEffect(() => {
    async function run() {
      if (drawImageQueue.length > 0 && runningJob.current === false) {
        runningJob.current = true;

        try {
          const currentJob = drawImageQueue[0];
          await currentJob();
          await new Promise((r) => setTimeout(r, 500));
        } catch (e) {
          console.error(e);
        } finally {
          runningJob.current = false;
          setDrawImageQueue((prev) => prev.filter((_, i) => i !== 0));
        }
      }
    }

    run();
  }, [drawImageQueue]);

  const setDrawImages = useCallback((scenes: (Scene | undefined)[]) => {
    const currentScenes = cloneDeep(scenes);
    const latestHashes = cloneDeep(hashes.current);

    const currentHashes = {
        ...latestHashes,
        ...(
          currentScenes
            .reduce((acc: { [id: string]: string }, scene) => {
              if (!scene) {
                return acc;
              }

              const key = {
                clips: scene.clips,
                background: scene.background,
              };

              return { ...acc, [scene.id]: md5(JSON.stringify(key)) };
            }, {})
        ),
      };

    const currentDraws = Object.entries(currentHashes)
      .reduce((acc: string[], [id, hash]) => {
        if (!latestHashes[id] || latestHashes[id] !== hash) {
          return [...acc, id];
        }

        return acc;
      }, []);

    hashes.current = currentHashes;

    // currentScenes.forEach((scene) => {
    //   if (!scene) {
    //     return;
    //   }
    //   if (!currentDraws.includes(scene.id)) {
    //     return;
    //   }
    //   getImage(scene).then((image) => {
    //     if (abortControllerRef.current.signal.aborted) {
    //       URL.revokeObjectURL(image);
    //       return;
    //     }
    //     setDrawImage((prev) => {
    //       if (hashes.current[scene.id] !== currentHashes[scene.id]) {
    //         URL.revokeObjectURL(image);
    //         return prev;
    //       }

    //       const prevImage = prev[scene.id];

    //       if (prevImage) {
    //         URL.revokeObjectURL(prevImage);
    //       }

    //       return {
    //         ...prev,
    //         [scene.id]: image,
    //       }
    //     });
    //   });
    // });

    currentScenes.forEach((scene) => {
      if (!scene) {
        return;
      }
      if (!currentDraws.includes(scene.id)) {
        return;
      }

      const job = async () => {
        const image = await getImage(scene);

        if (abortControllerRef.current.signal.aborted) {
          URL.revokeObjectURL(image);
          return;
        }
        setDrawImage((prev) => {
          if (hashes.current[scene.id] !== currentHashes[scene.id]) {
            URL.revokeObjectURL(image);
            return prev;
          }

          const prevImage = prev[scene.id];

          if (prevImage) {
            URL.revokeObjectURL(prevImage);
          }

          return {
            ...prev,
            [scene.id]: image,
          }
        });
      }

      setDrawImageQueue((prev) => {
        return [...prev, job]
      });
    });
  }, [getImage]);

  const onSetDrawImages = useEffectEvent((scenes: (Scene | undefined)[]) => {
    setDrawImages(scenes);
  });

  useEffect(() => {
    onSetDrawScripts(streamingScenes);
  }, [streamingScenes]);

  useEffect(() => {
    onSetDrawImages(streamingScenes);
  }, [streamingScenes]);

  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      abortControllerRef.current.abort();
      setDrawScript({});
      setDrawImage((prev) => {
        Object.values(prev).forEach((value) => {
          if (value) {
            URL.revokeObjectURL(value);
          }
        });
  
        return {};
      });
      hashes.current = {};
    }
  }, []);

  return {
    width,
    height,
    orientation,
    images: drawImage,
    scripts: drawScript,
  };
}

export function useAutomationStreamingInput() {
  const [uploadAutomationFile] = useUploadAutomationMutation();

  const insertFile = useCallback(async (params: {
    files?: File[];
    sample?: AutomationStreamingSampleDocs | null;
    uploadedFiles?: {
      azure: string;
      originalFileName: string;
      fileType: string;
    }[];
  }) => {
    const { files = [], sample, uploadedFiles = [] } = params;

    const uploaded = await Promise.all(files.map(async (file) => {
      const result = await uploadAutomationFile([file]).unwrap();

      if (!result.success) {
        return Promise.reject();
      }

      return {
        azure: result.data,
        originalFileName: file.name,
        fileType: file.type,
      }
    }));

    if (!uploaded) {
      throw new Error("Failed on uploading files.");
    }

    const result = [
      ...uploadedFiles,
      ...(
        sample
          ? [
            {
              azure: sample.files[0].azure,
              originalFileName: sample.files[0].name,
              fileType: sample.files[0].type,
            }
          ]
          : []
      ),
      ...uploaded,
    ];

    const input = {
      type: "docs",
      value: result,
    } as AutomationStreamingCreateParams["input"];

    const preset = (() => {
      if (sample) {
        return {
          sceneConfig: sample.sceneConfig,
          template: sample.template,
        }
      }

      const purpose = (() => {
        const filename = result[0].originalFileName;
  
        if (filename.startsWith("_KB_")) {
          return "ko_business";
        };
        if (filename.startsWith("_SFN_")) {
          return "ko_business2";
        };
        if (filename.startsWith("_REPORT_")) {
          return "business2"
        };
        return "business";
      })();

      return {
        sceneConfig: SCENE_CONFIG_MAP[purpose],
        template: null,
      }
    })() as AutomationStreamingCreateParams["preset"];

    return { input, preset };
  }, [uploadAutomationFile]);

  const insertUrl = useCallback((params: {
    publicUrl: string;
    sample?: AutomationStreamingSampleUrl | null;
  }) => {
    const { publicUrl, sample } = params;

    const preCheckUrlResult = preCheckUrl(publicUrl);

    if (!preCheckUrlResult) {
      throw new Error("Failed on checking URL.");
    }

    const input = {
      type: "url",
      value: [{ publicUrl }],
    } as AutomationStreamingCreateParams["input"];

    const preset = (() => {
      if (sample) {
        return {
          sceneConfig: sample.sceneConfig,
          template: sample.template,
        }
      }

      const purpose = preCheckUrlResult.purpose as keyof typeof SCENE_CONFIG_MAP;

      return {
        sceneConfig: SCENE_CONFIG_MAP[purpose],
        template: null,
      }
    })() as AutomationStreamingCreateParams["preset"];

    return { input, preset };
  }, []);

  const insertTopic = useCallback((params: { text: string }) => {
    const { text } = params;

    const { purpose } = preCheckTopic(text);

    const input = {
      type: "topic",
      value: [{ text }],
    } as AutomationStreamingCreateParams["input"];

    const preset = {
      sceneConfig: SCENE_CONFIG_MAP[purpose as keyof typeof SCENE_CONFIG_MAP],
      template: null,
    } as AutomationStreamingCreateParams["preset"];

    return { input, preset };
  }, []);

  return { insertFile, insertUrl, insertTopic };
}

export function useCreateAutomationStreamingProject() {
  const { fetch: fetchSubscription } = useSubscription();

  const check = useCallback(
    async (params: { input: AutomationStreamingCreateParams["input"] }) => {
      const { input } = params;

      if (!input) {
        return { success: false, reason: null };
      }

      const { plan, credits } = await fetchSubscription();

      const trackType = (() => {
        if (input.type !== "topic") {
          return `${input.type}_to_video`;
        }

        if (input.value[0].text.length > 100) {
          return "article_to_video";
        }

        return "topic_to_video";
      })();

      const trackContent = (() => {
        if (input.type === "url") {
          return "url";
        }
        if (input.type === "docs") {
          return "filename";
        }
        return "text";
      })();

      if (plan?.normalized !== "enterprise" && credits?.automationCredit === 0) {
        return { success: false, reason: "limit" };
      }

      if (plan?.normalized === "free") {
        try {
          const currentDate = dayjs().format("YYYY-MM-DD");
          const initCount = {
            date: currentDate,
            count: 0,
          };

          const localCount = (() => {
            const json = localStorage.getItem("automation-generate-count");
  
            if (!json) {
              localStorage.setItem("automation-generate-count", JSON.stringify(initCount));
              return initCount;
            }
  
            const parsed = JSON.parse(json);
  
            if (parsed.date !== currentDate) {
              localStorage.setItem("automation-generate-count", JSON.stringify(initCount));
              return initCount;
            }
  
            return parsed;
          })();

          if (localCount.count >= 10) {
            throw new Error("limit count");
          }

          const updatedCount = {
            date: localCount.date,
            count: localCount.count + 1,
          };

          localStorage.setItem("automation-generate-count", JSON.stringify(updatedCount));
        } catch (e) {
          return { success: false, reason: "limit" };
        }
      }

      if (window?.dataLayer?.push) {
        window.dataLayer.push({
          event: "first_generate",
          type: trackType,
          subscription: plan?.name,
          content: trackContent,
          goal: "auto",
          language: "auto",
          template_id: "auto",
          media: "auto",
          style: "auto",
        });

        window.dataLayer.push({
          event: "project_create",
          template_category: "auto",
          template_id: "auto",
          language_editor: "auto",
          subscription: plan?.name,
        });

        window.dataLayer.push({
          event: "generate",
          type: trackType,
          subscription: plan?.name,
          content: trackContent,
          goal: "auto",
          language: "auto",
          template_id: "auto",
          media: "auto",
          style: "auto",
        });
      }

      return { success: true };
    },
    [fetchSubscription]
  );

  const [createProject] = useCreateAutomationStreamingProjectMutation();

  const create = useCallback(async (params: AutomationStreamingCreateParams) => {
    return createProject(params).unwrap();
  }, [createProject]);

  const [resetProject] = useResetAutomationStreamingProjectMutation();

  const reset = useCallback(async (params: AutomationStreamingCreateParams & { projectId: string }) => {
    return resetProject(params).unwrap();
  }, [resetProject]);

  return { check, create, reset };
}

export function stripAutomationStreamingUrl(params: {
  router: NextRouter;
  targets?: string[];
  base?: string;
}) {
  const { router, targets = [], base } = params;

  const path = base ? base.split("?")[0] : router.asPath.split("?")[0];

  const slugs = router.pathname.split("/")
    .reduce((acc: string[], value) => {
      const reg = /\[{1,2}\.{0,3}(\w+?)\]{1,2}/g.exec(value);

      if (!reg) {
        return acc;
      }

      return [...acc, reg[1]];
    }, []);

  const urlSearchParams = (() => {
    const queries = Object.entries(router.query || {})
      .reduce((acc: typeof router.query, [key, value]) => {
        if (targets.includes(key) || slugs.includes(key)) {
          return acc;
        }
        return { ...acc, [key]: value };
      }, {});

    if (Object.keys(queries).length === 0) {
      return "";
    }

    const searchParams = new URLSearchParams();

    Object.entries(queries).forEach(([queryKey, queryValue]) => {
      if (Array.isArray(queryValue)) {
        queryValue.forEach((v) => searchParams.append(queryKey, v));
        return;
      }
      if (queryValue !== undefined) {
        searchParams.append(queryKey, queryValue);
        return;
      }
    });

    return `?${searchParams.toString()}`;
  })();

  return `${path}${urlSearchParams}`;
}